import { useState, useCallback, useRef, useEffect } from 'react';
import i18n from 'i18next';

import * as Yup from 'yup';

import { FormHandles } from '@unform/core';

import { useSnackbar } from 'notistack';

import getValidationErrors from 'utils/getValidationErrors';
import { SelectItem, transformToSelect } from 'utils/toSelect';

import { useGlobalLoading } from 'hooks/LoadingContext';

import { getCookie } from 'utils/cookies';

import {
  formSchema,
  IBatchData,
  IParamsGetBach,
  IParamsGetBachAPI,
} from '../types';
import { getBachsAPI, handleOptionsEstructure } from '../api';

export const useReportBatch = () => {
  const searchRef = useRef<FormHandles>(null);
  const clientId = getCookie('@pdamodules::codigoCliente');

  const { enqueueSnackbar } = useSnackbar();
  const { setGlobalLoading } = useGlobalLoading();

  const tzoffset = new Date().getTimezoneOffset() * 60000;
  const dateDay = new Date(Date.now() - tzoffset).toISOString().split('T')[0];

  const [valueInput, setValueInput] = useState<string>('');

  const [showTable, setShowTable] = useState<boolean>(false);

  const [batchsData, setBatchsData] = useState<IBatchData[]>([]);
  const [structureOptions, setStructureOptions] = useState<SelectItem[]>([]);

  const handleGetInputValue = (value: string) => setValueInput(value);

  const getStructureOptions = useCallback(async () => {
    await handleOptionsEstructure(clientId).then(({ data }) => {
      const options = transformToSelect(data);
      setStructureOptions(options);
    });
  }, [clientId]);

  const handleSubmit = useCallback(
    async (params: IParamsGetBach) => {
      try {
        setShowTable(false);
        setGlobalLoading(true);

        await formSchema.validate(params, {
          abortEarly: false,
        });

        const paramsApi: IParamsGetBachAPI = {
          codigoCliente: +clientId,
          codigoPedido: params.codigoPedido || null,
          tipo: params.tipo || null,
          produto: params.produto || null,
          lote: params.lote || null,
          serie: params.serie || null,
          dataInicio: params.dataInicio || null,
          dataFim: params.dataFim || null,
        };

        const { data: response } = await getBachsAPI(paramsApi);

        if (response.length <= 0) {
          setBatchsData([]);
          enqueueSnackbar(i18n.t('screens.commom.resultsNotFound'), {
            variant: 'warning',
          });
        } else {
          enqueueSnackbar(
            i18n.t(String(response.length), 'screens.commom.successFilter'),
            {
              variant: 'success',
            }
          );

          setBatchsData(response);
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const erros = getValidationErrors(err);
          searchRef.current?.setErrors(erros);
          console.error(err);
          setGlobalLoading(false);
          enqueueSnackbar(`${i18n.t('screens.commom.dateBetween')}`, {
            variant: 'warning',
          });
        }
      } finally {
        setShowTable(true);
        setGlobalLoading(false);
      }
    },
    [clientId, enqueueSnackbar, setGlobalLoading]
  );

  const resultsInputsList = useCallback(() => {
    if (
      searchRef.current?.getFieldValue('codigoPedido') !== '' ||
      searchRef.current?.getFieldValue('produto') !== ''
    ) {
      searchRef.current?.setFieldValue('tipo', '');
      searchRef.current?.setFieldValue('lote', '');
      searchRef.current?.setFieldValue('serie', '');
      searchRef.current?.clearField('dataInicio');
      searchRef.current?.clearField('dataFim');
    } else {
      searchRef.current?.setFieldValue('dataInicio', dateDay);
      searchRef.current?.setFieldValue('dataFim', dateDay);
    }
  }, [dateDay]);

  useEffect(() => {
    handleSubmit({ dataFim: dateDay, dataInicio: dateDay });
  }, [dateDay, handleSubmit]);

  useEffect(() => {
    getStructureOptions();
  }, [getStructureOptions]);

  useEffect(() => {
    resultsInputsList();
  }, [resultsInputsList, valueInput]);

  return {
    dateDay,
    searchRef,
    showTable,
    batchsData,
    structureOptions,
    handleSubmit,
    handleGetInputValue,
  };
};
